// FIXME: Update this file to be type safe and remove this and next line
// @ts-strict-ignore
import { Component, inject, Inject, OnDestroy } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { combineLatest, of, Subject, switchMap, takeUntil } from "rxjs";

import {
  CollectionAdminService,
  OrganizationUserApiService,
} from "@bitwarden/admin-console/common";
import {
  getOrganizationById,
  OrganizationService,
} from "@bitwarden/common/admin-console/abstractions/organization/organization.service.abstraction";
import {
  CollectionAdminView,
  CollectionView,
} from "@bitwarden/common/admin-console/models/collections";
import { Organization } from "@bitwarden/common/admin-console/models/domain/organization";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { FeatureFlag } from "@bitwarden/common/enums/feature-flag.enum";
import { ConfigService } from "@bitwarden/common/platform/abstractions/config/config.service";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import {
  DIALOG_DATA,
  DialogConfig,
  DialogRef,
  DialogService,
  ToastService,
} from "@bitwarden/components";
import { Vfo1I18nPipe, Vfo1TerminologyService } from "@bitwarden/vault";

import { SharedModule } from "../../../../shared";
import { GroupApiService, GroupView } from "../../core";
import {
  AccessItemType,
  AccessItemValue,
  AccessItemView,
  AccessSelectorModule,
  convertToPermission,
  convertToSelectionView,
  mapGroupToAccessItemView,
  mapUserToAccessItemView,
  PermissionMode,
} from "../../shared/components/access-selector";

export interface BulkCollectionsDialogParams {
  organizationId: string;
  collections: CollectionView[];
}

// FIXME: update to use a const object instead of a typescript enum
// eslint-disable-next-line @bitwarden/platform/no-enums
export enum BulkCollectionsDialogResult {
  Saved = "saved",
  Canceled = "canceled",
}

// FIXME(https://bitwarden.atlassian.net/browse/CL-764): Migrate to OnPush
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  imports: [SharedModule, AccessSelectorModule, Vfo1I18nPipe],
  selector: "app-bulk-collections-dialog",
  templateUrl: "bulk-collections-dialog.component.html",
})
export class BulkCollectionsDialogComponent implements OnDestroy {
  private readonly vfo1TerminologyService = inject(Vfo1TerminologyService);

  protected readonly PermissionMode = PermissionMode;

  protected formGroup = this.formBuilder.group({
    access: [[] as AccessItemValue[]],
  });
  protected loading = true;
  protected organization: Organization;
  protected accessItems: AccessItemView[] = [];
  protected numCollections: number;

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(DIALOG_DATA) private params: BulkCollectionsDialogParams,
    private dialogRef: DialogRef<BulkCollectionsDialogResult>,
    private formBuilder: FormBuilder,
    private organizationService: OrganizationService,
    private accountService: AccountService,
    private groupService: GroupApiService,
    private organizationUserApiService: OrganizationUserApiService,
    private platformUtilsService: PlatformUtilsService,
    private i18nService: I18nService,
    private collectionAdminService: CollectionAdminService,
    private toastService: ToastService,
    private configService: ConfigService,
  ) {
    this.numCollections = this.params.collections.length;
    const organization$ = this.accountService.activeAccount$.pipe(
      switchMap((account) =>
        this.organizationService
          .organizations$(account?.id)
          .pipe(getOrganizationById(this.params.organizationId)),
      ),
    );
    const groups$ = organization$.pipe(
      switchMap((organization) => {
        if (!organization.useGroups) {
          return of([] as GroupView[]);
        }
        return this.groupService.getAll(organization.id);
      }),
    );
    const collections$ = this.accountService.activeAccount$.pipe(
      getUserId,
      switchMap((userId) =>
        this.collectionAdminService.collectionAdminViews$(this.params.organizationId, userId),
      ),
    );

    combineLatest([
      organization$,
      groups$,
      this.organizationUserApiService.getAllMiniUserDetails(this.params.organizationId),
      collections$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([organization, groups, users, collections]) => {
        this.organization = organization;

        this.accessItems = [].concat(
          groups.map(mapGroupToAccessItemView),
          users.data.map(mapUserToAccessItemView),
        );

        const selectedIds = new Set(this.params.collections.map((c) => c.id));
        const selectedCollections = collections.filter((c) => selectedIds.has(c.id));
        this.formGroup.controls.access.setValue(sharedAccess(selectedCollections));

        this.loading = false;
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submit = async () => {
    const users = this.formGroup.controls.access.value
      .filter((v) => v.type === AccessItemType.Member)
      .map(convertToSelectionView);

    const groups = this.formGroup.controls.access.value
      .filter((v) => v.type === AccessItemType.Group)
      .map(convertToSelectionView);

    await this.collectionAdminService.bulkAssignAccess(
      this.organization.id,
      this.params.collections.map((c) => c.id),
      users,
      groups,
    );

    const batchBarEnabled = await this.configService.getFeatureFlag(
      FeatureFlag.PM37785_VaultBatchBar,
    );
    const vfo1Enabled = this.vfo1TerminologyService.enabled();
    const singular = vfo1Enabled ? "sharedFolderEdited" : "collectionEdited";
    const plural = vfo1Enabled ? "sharedFoldersEdited" : "collectionsEdited";
    const editedMessage = batchBarEnabled
      ? this.i18nService.t(this.params.collections.length === 1 ? singular : plural)
      : this.i18nService.t(plural);

    this.toastService.showToast({
      variant: "success",
      title: null,
      message: editedMessage,
    });

    await this.dialogRef.close(BulkCollectionsDialogResult.Saved);
  };

  static open(dialogService: DialogService, config: DialogConfig<BulkCollectionsDialogParams>) {
    return dialogService.open<BulkCollectionsDialogResult, BulkCollectionsDialogParams>(
      BulkCollectionsDialogComponent,
      config,
    );
  }
}

/**
 * Determines the access to pre-populate the selector with for the selected collections. A single
 * collection uses its own access; multiple collections only pre-populate when they all share the
 * exact same access, otherwise the selector starts empty.
 */
function sharedAccess(collections: CollectionAdminView[]): AccessItemValue[] {
  if (collections.length === 0) {
    return [];
  }

  const [first, ...rest] = collections.map(mapToAccessSelections);
  return rest.every((access) => accessEquals(first, access)) ? first : [];
}

function mapToAccessSelections(collection: CollectionAdminView): AccessItemValue[] {
  return [].concat(
    collection.groups.map<AccessItemValue>((selection) => ({
      id: selection.id,
      type: AccessItemType.Group,
      permission: convertToPermission(selection),
    })),
    collection.users.map<AccessItemValue>((selection) => ({
      id: selection.id,
      type: AccessItemType.Member,
      permission: convertToPermission(selection),
    })),
  );
}

function accessEquals(a: AccessItemValue[], b: AccessItemValue[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const key = (value: AccessItemValue) => `${value.type}:${value.id}:${value.permission}`;
  const bKeys = new Set(b.map(key));
  return a.every((value) => bKeys.has(key(value)));
}
